/**
 * Standalone Junction Labs API methods for the questionnaire app.
 */

import axios from "axios";
import { getApiErrorMessage } from "../../../services/api";
import { normalizePatientProfile, normalizePhone } from "./labCheckoutUtils";
import { ALLOWED_AVAILABILITY_RADII } from "./labConstants";
import { labEndpoints } from "./labEndpoints";

function _baseLabHeaders() {
  const headers = { "Content-Type": "application/json" };
  const clientId = localStorage.getItem("welliemd_client_id");
  if (clientId) headers["X-Client-ID"] = clientId;
  return headers;
}

function _publicLabHeaders() {
  return _baseLabHeaders();
}

function _labHeaders() {
  const apiToken = process.env.REACT_APP_API_TOKEN || "";
  const headers = _baseLabHeaders();
  if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;
  const patientToken =
    localStorage.getItem("patient_access_token") ||
    sessionStorage.getItem("patient_access_token") ||
    sessionStorage.getItem("existing_patient_access_token");
  if (patientToken) headers["Authorization"] = `Bearer ${patientToken}`;
  return headers;
}

function _tenantEndpoint() {
  const endpoint = localStorage.getItem("welliemd_client_api_endpoint");
  if (!endpoint) throw new Error("Client API endpoint not found. Please refresh the page.");
  return endpoint;
}

export async function getPublicLabBrandSettings(clientApiEndpoint) {
  const base = String(clientApiEndpoint || _tenantEndpoint()).replace(/\/+$/, "");
  try {
    const { data } = await axios.get(`${base}/api/v1/public/brand-settings/`, {
      headers: _publicLabHeaders(),
      timeout: 10000,
    });
    return data && typeof data === "object" ? data : {};
  } catch (error) {
    throw _wrappedApiError(error, "Could not load client branding.");
  }
}

function _storePatientSession(data) {
  const access = data?.access || data?.access_token;
  if (access) {
    localStorage.setItem("patient_access_token", access);
    sessionStorage.setItem("patient_access_token", access);
  }
  if (data?.user) {
    sessionStorage.setItem("patient_user", JSON.stringify(data.user));
  }
}

function _normalizePatientFromLogin(data) {
  return normalizePatientProfile({
    ...(data?.patient || {}),
    ...(data?.user || {}),
    id: data?.patient?.id || data?.patient_id || data?.user?.patient_id,
    dob: data?.dob || data?.patient?.dob || data?.patient?.date_of_birth || data?.user?.dob,
    sex_at_birth:
      data?.sex_at_birth ||
      data?.patient?.sex_at_birth ||
      data?.patient?.sex ||
      data?.user?.sex_at_birth ||
      data?.user?.sex ||
      data?.user?.gender,
  });
}

function _wrappedApiError(error, fallback) {
  const d = error?.response?.data;
  const isGenericStatusError = /^Request failed with status code/i.test(error?.message || "");
  let message = getApiErrorMessage(error);
  if ((!d || typeof d !== "object" || isGenericStatusError) && fallback) {
    message = fallback;
  }
  const wrapped = new Error(message || fallback || "Something went wrong.");
  wrapped.code = error?.response?.data?.code || null;
  wrapped.accountStatus = error?.response?.data?.account_status || null;
  wrapped.status = error?.response?.status || null;
  return wrapped;
}

export async function lookupLabPatientEmail(email) {
  const base = _tenantEndpoint();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail) return { exists: false, accountStatus: "new" };
  try {
    const { data } = await axios.post(
      labEndpoints.emailLookup(base),
      { email: normalizedEmail },
      { headers: _publicLabHeaders(), timeout: 30000 }
    );
    const exists = Boolean(data?.exists);
    return {
      exists,
      accountStatus: data?.account_status || (exists ? "active_patient" : "new"),
    };
  } catch (error) {
    throw _wrappedApiError(error, "We could not check your email just now. Please try again.");
  }
}

export async function loginLabPatient(email, password) {
  const base = _tenantEndpoint();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  localStorage.removeItem("patient_access_token");
  sessionStorage.removeItem("patient_access_token");
  try {
    const { data } = await axios.post(
      labEndpoints.login(base),
      { email: normalizedEmail, password, portal: "patient" },
      {
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("welliemd_client_id") && {
            "X-Client-ID": localStorage.getItem("welliemd_client_id"),
          }),
        },
        timeout: 30000,
        withCredentials: true,
      }
    );
    _storePatientSession(data);
    return { access: data?.access || data?.access_token, patient: _normalizePatientFromLogin(data), raw: data };
  } catch (error) {
    throw _wrappedApiError(error, "Login failed.");
  }
}

export async function createLabPatientAccount({ email, phone, password, dob, sex }) {
  const base = _tenantEndpoint();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedEmail) throw new Error("Email is required.");
  if (!password) throw new Error("Password is required.");
  if (!normalizedPhone) throw new Error("Phone is required.");
  try {
    const { data } = await axios.post(
      labEndpoints.onboarding(base),
      {
        email: normalizedEmail,
        phone: normalizedPhone,
        password,
        ...(dob ? { date_of_birth: dob } : {}),
        ...(sex ? { sex } : {}),
      },
      { headers: _publicLabHeaders(), timeout: 60000 }
    );
    if (!data?.patient_id && !data?.success) {
      throw new Error(data?.error || "Failed to create patient account.");
    }
    return data;
  } catch (error) {
    throw _wrappedApiError(error, "Failed to create patient account.");
  }
}

export async function getLabCheckoutPaymentMethods() {
  const base = _tenantEndpoint();
  try {
    const { data } = await axios.get(
      labEndpoints.paymentMethods(base),
      { headers: _labHeaders(), timeout: 30000 }
    );
    return Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || "Failed to load saved payment methods.");
  }
}

export async function getLabCheckoutSession() {
  const base = _tenantEndpoint();
  try {
    const { data } = await axios.get(
      labEndpoints.paymentMethods(base),
      { headers: _labHeaders(), timeout: 30000 }
    );
    return {
      patient: data?.patient ? normalizePatientProfile(data.patient) : null,
      paymentMethods: Array.isArray(data?.results) ? data.results : [],
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || "Failed to load checkout session.");
  }
}

export async function updateLabCheckoutPatientProfile({ phone, dob, sex, address }) {
  const base = _tenantEndpoint();
  const sexForProfile = (() => {
    const value = String(sex || "").trim().toLowerCase();
    if (value === "male") return "Male";
    if (value === "female") return "Female";
    if (value === "other") return "Other";
    return sex || "";
  })();
  const payload = {
    ...(phone ? { phone: normalizePhone(phone) } : {}),
    ...(dob ? { date_of_birth: dob } : {}),
    ...(sexForProfile ? { sex: sexForProfile } : {}),
    ...(address?.street ? { address: address.street } : {}),
    ...(address?.apt ? { address_line_2: address.apt } : {}),
    ...(address?.city ? { city: address.city } : {}),
    ...(address?.state ? { state: String(address.state).toUpperCase() } : {}),
    ...(address?.zip ? { zip_code: address.zip } : {}),
  };
  try {
    const { data } = await axios.patch(
      labEndpoints.patientProfile(base),
      payload,
      { headers: _labHeaders(), timeout: 30000 }
    );
    return normalizePatientProfile(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || "Failed to save patient details.");
  }
}

/**
 * Fetch lab panel details by storefront slug.
 * Now preserves is_combined, combined_methods, collection_method_display,
 * biomarker_count, patient_price, discounted_patient_price, service_states.
 *
 * For combined storefronts, combined_methods[] each contain:
 *   { method, label, assignment_id, junction_lab_test_id, patient_price, service_states, lab_provider }
 *
 * For single-method storefronts, synthesizes a single-element collectionOptions[]
 * so StepCollection always works with the same shape.
 */
export async function getStorefrontLab(slug) {
  const base = _tenantEndpoint();
  try {
    const { data } = await axios.get(
      labEndpoints.storefront(base, slug),
      { headers: _publicLabHeaders(), timeout: 15000 }
    );
    if (!data) throw new Error("Lab panel not found");

    const METHOD_LABELS = {
      at_home_phlebotomy: "At-home phlebotomy",
      walk_in_test: "Walk-in lab draw",
      testkit: "At-home test kit",
      on_site_collection: "On-site collection",
    };

    const rawPrice = parseFloat(
      data.patient_price?.amount ?? data.price?.amount ?? data.patient_price ?? data.price ?? 0
    ) || 0;
    const rawDiscount = data.discounted_patient_price?.amount ?? data.discounted_patient_price;
    const discountedPrice = rawDiscount != null ? parseFloat(rawDiscount) || null : null;

    const isCombined = Boolean(data.is_combined);
    const rawMethod = data.collection_method || data.collection || "";

    // Build collectionOptions: unified shape for StepCollection regardless of storefront type.
    let collectionOptions = [];
    if (isCombined && Array.isArray(data.combined_methods) && data.combined_methods.length > 0) {
      collectionOptions = data.combined_methods.map((m) => ({
        method: m.method || "",
        label: m.label || METHOD_LABELS[m.method] || m.method,
        assignment_id: m.assignment_id || "",
        junction_lab_test_id: m.junction_lab_test_id || "",
        patient_price: parseFloat(m.patient_price?.amount ?? m.patient_price ?? rawPrice) || rawPrice,
        discounted_price: m.discounted_patient_price
          ? parseFloat(m.discounted_patient_price?.amount ?? m.discounted_patient_price) || null
          : discountedPrice,
        service_states: m.service_states || [],
        lab_provider: m.lab_provider || data.lab_provider || "",
        is_orderable: m.is_orderable !== false,
        aoe_questions: Array.isArray(m.aoe_questions) ? m.aoe_questions : [],
        aoe_required_count: m.aoe_required_count || 0,
        aoe_optional_count: m.aoe_optional_count || 0,
      }));
    } else {
      // Single-method — synthesize one option
      collectionOptions = [
        {
          method: rawMethod,
          label: METHOD_LABELS[rawMethod] || rawMethod.replace(/_/g, " "),
          assignment_id: data.assignment_id || "",
          junction_lab_test_id: data.junction_lab_test_id || "",
          patient_price: discountedPrice ?? rawPrice,
          discounted_price: discountedPrice,
          service_states: data.service_states || [],
          lab_provider: data.lab_provider || data.lab || "",
          is_orderable: true,
          aoe_questions: Array.isArray(data.aoe_questions) ? data.aoe_questions : [],
          aoe_required_count: data.aoe_required_count || 0,
          aoe_optional_count: data.aoe_optional_count || 0,
        },
      ];
    }

    return {
      id: data.id || data.slug || slug,
      slug: data.slug || slug,
      category: data.category || "",
      name: data.name || "",
      description: data.description || "",
      image_url: data.image_url || "",
      collection_method: rawMethod,
      collection_method_display:
        data.collection_method_display ||
        METHOD_LABELS[rawMethod] ||
        rawMethod.replace(/_/g, " "),
      lab: data.lab_provider || data.lab || "",
      sample: data.sample_type || data.sample || "",
      fasting: data.fasting_required ? "Required" : "Not required",
      turnaround: data.common_turnaround_time || data.worst_case_turnaround_time || data.turnaround || "",
      biomarkers: Array.isArray(data.biomarkers)
        ? data.biomarkers.map((b) => (typeof b === "string" ? b : b.name || ""))
        : [],
      biomarker_count: data.biomarker_count || (Array.isArray(data.biomarkers) ? data.biomarkers.length : 0),
      aoe_questions: Array.isArray(data.aoe_questions) ? data.aoe_questions : [],
      price: rawPrice,
      discounted_price: discountedPrice,
      // Effective price shown to patient (discounted if available)
      display_price: discountedPrice ?? rawPrice,
      service_states: data.service_states || [],
      is_combined: isCombined,
      combined_methods: isCombined ? (data.combined_methods || []) : [],
      collectionOptions,
      junction_status: data.junction_status || "",
      operational_status: data.operational_status || "",
    };
  } catch (error) {
    if (error.response?.status === 404) throw new Error("Lab panel not found or unavailable.");
    throw new Error(getApiErrorMessage(error) || "Failed to load lab panel.");
  }
}

export async function getLabCheckoutPolicies(slug) {
  const base = _tenantEndpoint();
  try {
    const { data } = await axios.get(
      labEndpoints.policies(base, slug),
      { headers: _publicLabHeaders(), timeout: 15000 }
    );
    return {
      policies: data?.policies || {},
      missing: Array.isArray(data?.missing) ? data.missing : [],
      isReady: Boolean(data?.is_ready),
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || "Failed to load checkout policies.");
  }
}

// Allowed radii that Junction supports.
/**
 * Check Junction availability for a patient's location.
 * Accepts optional radius (default "25"). Invalid values are coerced to "25".
 *
 * Returns a normalized response:
 * {
 *   is_available: bool,
 *   is_combined: bool,
 *   source: string,
 *   reason: string | null,
 *   // Single-method:
 *   collection_method: string,
 *   phlebotomy_served: bool,
 *   psc_count: int,
 *   drawSites: DrawSite[],  // normalized from providers when available
 *   // Combined:
 *   serviceable_methods: ServiceableMethod[],  // each has drawSites[]
 * }
 */
export async function checkLabAvailability(
  slug,
  zipCode,
  state = "",
  radius = "25",
  ownership = {},
) {
  const base = _tenantEndpoint();
  const safeRadius = ALLOWED_AVAILABILITY_RADII.has(String(radius)) ? String(radius) : "25";
  const params = new URLSearchParams({ zip_code: zipCode, radius: safeRadius });
  if (state) params.set("state", state);
  if (ownership?.programId) params.set("program_id", String(ownership.programId));
  if (ownership?.requirementId) params.set("requirement_id", String(ownership.requirementId));
  try {
    const { data } = await axios.get(
      labEndpoints.availability(base, slug, params.toString()),
      { headers: _publicLabHeaders(), timeout: 15000 }
    );
    return _normalizeAvailabilityResponse(data);
  } catch (error) {
    if (error.response?.status === 400 && error.response?.data && error.response.data.is_available === false) {
      return _normalizeAvailabilityResponse(error.response.data);
    }
    if (error.response?.status === 404) {
      return { is_available: false, reason: "Lab storefront is unavailable.", source: "welliemd_validation", drawSites: [] };
    }
    throw new Error(getApiErrorMessage(error) || "Availability check failed.");
  }
}

/**
 * Normalize raw availability response into a frontend-friendly shape with drawSites[].
 * If backend returns psc_count only (no site details), drawSites is empty and the UI
 * shows a count-only confirmation.
 */
function _normalizeAvailabilityResponse(data) {
  if (!data || typeof data !== "object") {
    return { is_available: false, reason: "Availability check failed.", source: "welliemd_validation", drawSites: [] };
  }

  const normalizeSites = (providers) => {
    if (!Array.isArray(providers)) return [];
    const flattened = providers.flatMap((provider) => {
      if (!provider || typeof provider !== "object") return [];
      const nested = provider.patient_service_centers || provider.sites || provider.locations;
      if (!Array.isArray(nested)) return [provider];
      const network = provider.provider_network || provider.lab_name || provider.name || provider.slug || "";
      return nested.map((site) => ({
        ...(site || {}),
        provider_network: site?.provider_network || network,
      }));
    });
    return flattened
      .map((p) => {
        const metadata = p.metadata && typeof p.metadata === "object" ? p.metadata : {};
        const firstLine = metadata.first_line || metadata.line1 || p.first_line || p.line1 || p.street || p.street_address || "";
        const secondLine = metadata.second_line || metadata.line2 || p.second_line || "";
        return {
          name: metadata.name || p.name || p.site_name || p.location_name || p.facility_name || "",
          address:
            (typeof p.address === "string" ? p.address : "") ||
            [firstLine, secondLine].filter(Boolean).join(", ") ||
            p.address_line_1 ||
            p.address?.street ||
            p.address?.line1 ||
            "",
          city: metadata.city || p.city || p.address?.city || "",
          state: metadata.state || p.state || p.address?.state || "",
          zip: metadata.zip_code || metadata.zipcode || p.zip || p.zipcode || p.zip_code || p.postal_code || p.address?.zip || p.address?.zipcode || "",
          distance_miles: p.distance_miles ?? p.distance ?? p.distance_in_miles ?? null,
          hours: Array.isArray(metadata.hours || p.hours)
            ? (metadata.hours || p.hours).join(", ")
            : metadata.hours || p.hours || p.operating_hours || p.opening_hours || "",
          provider_network: p.provider_network || p.network || p.lab_name || p.provider || "",
        };
      })
      .filter((p) => p.name || p.address);
  };

  if (data.is_combined) {
    const serviceableMethods = (data.serviceable_methods || []).map((m) => ({
      ...m,
      drawSites: normalizeSites(m.providers || []),
    }));
    return {
      ...data,
      is_combined: true,
      serviceable_methods: serviceableMethods,
    };
  }

  return {
    ...data,
    drawSites: normalizeSites(data.providers || []),
  };
}

export async function submitLabCheckout(slug, payload) {
  const base = _tenantEndpoint();
  const body = { slug, ...payload };
  try {
    const { data } = await axios.post(
      labEndpoints.checkout(base),
      body,
      { headers: _labHeaders(), timeout: 90000 }
    );
    if (data.success) {
      return {
        success: true,
        orderNumber: data.orderNumber || data.display_id || "",
        message: data.message || "Your lab order has been created.",
        steps: data.steps || [],
        reimbursement: data.reimbursement || null,
      };
    }
    throw new Error(data.message || data.error || "Checkout failed.");
  } catch (error) {
    const responseData = error.response?.data || {};
    const fieldMessage = [
      responseData.collection,
      responseData.address,
      responseData.payment,
      responseData.phone,
      responseData.detail,
      responseData.message,
      responseData.error,
    ]
      .map((value) => Array.isArray(value) ? value[0] : value)
      .find((value) => typeof value === "string" && value.trim());
    const msg = fieldMessage || getApiErrorMessage(error);
    const enhancedError = new Error(msg || "An error occurred during checkout.");
    enhancedError.steps = responseData.steps || [];
    enhancedError.orderNumber = responseData.orderNumber || responseData.display_id || "";
    enhancedError.errorCode = responseData.error_code || "";
    enhancedError.status = error.response?.status || null;
    enhancedError.detail = responseData.error || responseData.detail || "";
    enhancedError.retryable = responseData.retryable || false;
    throw enhancedError;
  }
}

/**
 * Re-fetches the lab storefront payment methods simply to confirm the 
 * active_gateway hasn't changed right before submit.
 */
export async function refreshPaymentConfig() {
  const base = _tenantEndpoint();
  try {
    const { data } = await axios.get(
      labEndpoints.paymentMethods(base),
      { headers: _labHeaders(), timeout: 10000 }
    );
    return data.active_gateway || "stripe";
  } catch (error) {
    console.error("Failed to refresh lab payment config:", error);
    return null;
  }
}
